import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ShieldCheck, Inbox, ScrollText, HardDrive, Users, Activity,
  Eye, MousePointerClick, BarChart3,
} from "lucide-react";

function Card({ label, value, sub, icon: I, testId }) {
  return (
    <div data-testid={testId} className="card-elev p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-navy/60">{label}</span>
        <I size={18} className="text-gold" />
      </div>
      <div className="mt-4 text-4xl font-serif text-navy">{value}</div>
      {sub && <div className="mt-1 text-xs text-navy/60">{sub}</div>}
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [homeStats, setHomeStats] = useState(null);
  const [visits, setVisits] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/homepage-stats").then((r) => setHomeStats(r.data)).catch(() => {});
    api.get("/admin/analytics/summary?days=30")
      .then((r) => setVisits(r.data)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-navy/60">Loading…</div>;

  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const vt = visits?.totals || {};
  const vtoday = visits?.today || {};

  return (
    <div data-testid="admin-overview">
      <div>
        <span className="eyebrow">Governance Dashboard</span>
        <h1 className="mt-2 text-3xl text-navy">Platform Overview</h1>
        <span className="divider-gold" />
      </div>

      {/* Visitor Analytics (aggregate, privacy-preserving, no PostHog dependency) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="eyebrow">Visitor Analytics · last 30 days</span>
            <div className="text-xs text-navy/50 mt-1">
              Aggregate only · No cookies · No PII · Hashes rotate daily
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Card testId="stat-visitors" label="Unique visitors" icon={Users}
                value={vt.visitors ?? "—"}
                sub={`${vtoday.visitors ?? 0} today`} />
          <Card testId="stat-sessions" label="Sessions" icon={MousePointerClick}
                value={vt.sessions ?? "—"}
                sub="30-min bucketed" />
          <Card testId="stat-pageviews" label="Page views" icon={Eye}
                value={vt.pageviews ?? "—"}
                sub={`${vtoday.pageviews ?? 0} today`} />
          <Card testId="stat-analytics-consents" label="Analytics consents" icon={BarChart3}
                value={vt.consents_analytics ?? "—"}
                sub={`${vt.consents_necessary_only ?? 0} necessary-only`} />
        </div>

        {/* Visitors by Day + by Hour — computed from existing visit_events. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          {/* Visitors by Day */}
          <div className="card-elev p-5" data-testid="chart-visitors-by-day">
            <div className="text-xs uppercase tracking-widest text-navy/60">Visitors by day · last {visits?.window_days ?? 30}d</div>
            {(() => {
              const rows = visits?.series || [];
              if (!rows.length) return <div className="mt-4 text-navy/50 text-sm">No visits recorded yet.</div>;
              const max = Math.max(1, ...rows.map((r) => r.visitors || 0));
              return (
                <div className="mt-4 flex items-end gap-[3px] h-32">
                  {rows.map((r) => {
                    const pct = Math.round(((r.visitors || 0) / max) * 100);
                    return (
                      <div key={r.day} className="flex-1 min-w-[3px] bg-gold/20 relative group"
                           style={{ height: `${Math.max(pct, 2)}%` }}
                           title={`${r.day}: ${r.visitors} visitors · ${r.sessions} sessions · ${r.pageviews} pv`}
                           data-testid={`bar-day-${r.day}`}>
                        <div className="absolute inset-0 bg-gold opacity-70 group-hover:opacity-100 transition-opacity" />
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="mt-2 flex justify-between text-[10px] text-navy/40">
              <span>{visits?.series?.[0]?.day || ""}</span>
              <span>{visits?.series?.[visits.series.length - 1]?.day || ""}</span>
            </div>
          </div>

          {/* Visitors by Hour */}
          <div className="card-elev p-5" data-testid="chart-visitors-by-hour">
            <div className="text-xs uppercase tracking-widest text-navy/60">
              Visitors by hour · UTC · last {visits?.window_days ?? 30}d
            </div>
            {(() => {
              const rows = visits?.by_hour || [];
              const max = Math.max(1, ...rows.map((r) => r.sessions || 0));
              if (!rows.length || max === 1 && rows.every((r) => !r.sessions)) {
                return <div className="mt-4 text-navy/50 text-sm">No hourly data yet.</div>;
              }
              return (
                <div className="mt-4 flex items-end gap-[3px] h-32">
                  {rows.map((r) => {
                    const pct = Math.round(((r.sessions || 0) / max) * 100);
                    return (
                      <div key={r.hour} className="flex-1 bg-navy/10 relative"
                           style={{ height: `${Math.max(pct, 2)}%` }}
                           title={`${String(r.hour).padStart(2, "0")}:00 UTC — ${r.sessions} sessions`}
                           data-testid={`bar-hour-${r.hour}`}>
                        <div className="absolute inset-0 bg-navy/70" />
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="mt-2 flex justify-between text-[10px] text-navy/40">
              <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
            </div>
          </div>
        </div>

        {/* Visitors by Country — CF-IPCountry only. No IP is stored. */}
        <div className="card-elev p-5 mt-5" data-testid="chart-visitors-by-country">
          <div className="flex items-baseline justify-between">
            <div className="text-xs uppercase tracking-widest text-navy/60">Visitors by country · top 10</div>
            <div className="text-[10px] text-navy/40">Derived from Cloudflare edge · country code only</div>
          </div>
          {(() => {
            const rows = (visits?.by_country || []).slice(0, 10);
            if (!rows.length) {
              return (
                <div className="mt-3 text-sm text-navy/60">
                  No country data yet. Traffic outside Cloudflare (e.g. preview) is stored as N/A.
                </div>
              );
            }
            const max = Math.max(1, ...rows.map((r) => r.visitors || 0));
            const fmtName = (cc) => {
              try { return new Intl.DisplayNames(["en"], { type: "region" }).of(cc) || cc; }
              catch (_e) { return cc; }
            };
            const flag = (cc) => {
              if (!cc || cc.length !== 2) return "";
              return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 0x1f1a5 + c.charCodeAt(0)));
            };
            return (
              <div className="mt-4 space-y-2">
                {rows.map((r) => {
                  const pct = Math.round(((r.visitors || 0) / max) * 100);
                  return (
                    <div key={r.country} className="flex items-center gap-3" data-testid={`row-country-${r.country}`}>
                      <div className="w-40 flex items-center gap-2 text-sm text-navy">
                        <span aria-hidden>{flag(r.country)}</span>
                        <span className="text-navy/80">{r.country}</span>
                        <span className="text-navy/50 text-xs truncate">{fmtName(r.country)}</span>
                      </div>
                      <div className="flex-1 h-2 bg-navy/5 rounded-sm overflow-hidden">
                        <div className="h-full bg-gold" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                      <div className="w-12 text-right text-sm text-navy tabular-nums">{r.visitors}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card testId="stat-submissions" label="Total submissions" value={stats.submissions_total} sub={`${stats.submissions_new} new`} icon={Inbox} />
        <Card testId="stat-today" label="Today" value={stats.submissions_today} sub="last 24 h" icon={Activity} />
        <Card testId="stat-campaigns" label="Active campaigns" value={homeStats?.campaigns_active ?? stats.campaigns_active} icon={Users} />
        <Card testId="stat-policies" label="Governance Documents" value={homeStats?.policies_total ?? stats.policies_total} sub={`Updated ${fmtTime(stats.last_policy_update)}`} icon={ScrollText} />
        <Card testId="stat-services" label="Live services" value={homeStats?.services_total ?? "—"} sub="active + published" icon={ShieldCheck} />
        <Card testId="stat-articles" label="Published articles" value={homeStats?.articles_total ?? "—"} icon={ScrollText} />
        <Card testId="stat-testimonials" label="Live testimonials" value={homeStats?.testimonials_total ?? "—"} icon={Users} />
        <Card testId="stat-consents" label="Consents recorded" value={stats.consents} icon={ShieldCheck} />
        <Card testId="stat-audit" label="Audit entries" value={stats.audit_entries} sub={`Last: ${fmtTime(stats.last_audit_at)}`} icon={HardDrive} />
      </div>

      <div className="mt-10 card-elev p-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-gold" />
          <div>
            <div className="text-navy font-medium">GDPR Compliance</div>
            <div className="text-xs uppercase tracking-widest text-navy/60">Status: {stats.gdpr_status}</div>
          </div>
          <span className="ms-auto chip !bg-white !text-navy !border-navy/20">Compliant</span>
        </div>
      </div>
    </div>
  );
}
